import type { OntologyTermListing, TermCard, TermKind, TermLabel } from "../../../api/content/ontologyApi";
import {
  ONTOLOGY_ENTITY_TYPES,
  ONTOLOGY_ROOT_IRIS,
  type LocalizedValue,
  type OntologyEntity,
  type OntologyEntityType,
  type OntologyViewerModel,
} from "./types";

/**
 * The viewer's model from the server's term cards: one tree per kind of term.
 *
 * Each tree hangs below its root (owl:Thing, the top properties, or a grouping root). A
 * term is placed below each parent of the same kind that the version declares; a term
 * with no such parent sits directly below the root, and so does a term caught in a cycle
 * of parents that never reaches the root, so every term can be found in its tree.
 */

const ENTITY_TYPE_OF_KIND: Record<TermKind, OntologyEntityType> = {
  class: "classes",
  object_property: "objectProperties",
  datatype_property: "dataProperties",
  annotation_property: "annotationProperties",
  individual: "individuals",
};

export function entityTypeOfKind(kind: TermKind): OntologyEntityType {
  return ENTITY_TYPE_OF_KIND[kind];
}

export function buildOntologyViewerModel(listing: OntologyTermListing, preferredLanguage: string): OntologyViewerModel {
  const entitiesByType = Object.fromEntries(
    ONTOLOGY_ENTITY_TYPES.map((type) => [
      type,
      buildTree(
        type,
        listing.terms.filter((term) => entityTypeOfKind(term.kind) === type),
        preferredLanguage,
      ),
    ]),
  ) as Record<OntologyEntityType, OntologyEntity[]>;
  return { source: { version: listing.version }, entitiesByType };
}

function buildTree(type: OntologyEntityType, terms: readonly TermCard[], preferredLanguage: string): OntologyEntity[] {
  const rootIri = ONTOLOGY_ROOT_IRIS[type];
  const declared = new Set(terms.map((term) => term.iri));
  const entities = new Map<string, OntologyEntity>(
    terms
      .filter((term) => term.iri !== rootIri)
      .map((term) => [
        term.iri,
        {
          iri: term.iri,
          type,
          label: orderedLabels(term.labels, preferredLanguage),
          definition: term.definition === null ? [] : [{ value: term.definition }],
          parents: term.parents.filter((parent) => parent !== term.iri && (declared.has(parent) || parent === rootIri)),
          children: [],
          domain: [...term.domain],
          range: [...term.range],
        },
      ]),
  );
  const root: OntologyEntity = {
    iri: rootIri,
    type,
    label: [],
    definition: [],
    parents: [],
    children: [],
    domain: [],
    range: [],
  };
  for (const entity of entities.values()) {
    const parents = entity.parents.filter((parent) => parent !== rootIri);
    if (parents.length === 0) {
      root.children.push(entity.iri);
    }
    for (const parent of parents) {
      entities.get(parent)?.children.push(entity.iri);
    }
  }
  const reachable = reachableFrom(root, entities);
  for (const entity of entities.values()) {
    if (!reachable.has(entity.iri)) {
      root.children.push(entity.iri);
      reachable.add(entity.iri);
      reachableFrom(entity, entities).forEach((iri) => reachable.add(iri));
    }
  }
  return [root, ...entities.values()];
}

function reachableFrom(start: OntologyEntity, entities: ReadonlyMap<string, OntologyEntity>): Set<string> {
  const reached = new Set<string>();
  const pending = [...start.children];
  for (let iri = pending.pop(); iri !== undefined; iri = pending.pop()) {
    if (reached.has(iri)) {
      continue;
    }
    reached.add(iri);
    pending.push(...(entities.get(iri)?.children ?? []));
  }
  return reached;
}

/** Labels in the user's language first, then English, then without a language, then the rest. */
export function orderedLabels(labels: readonly TermLabel[], preferredLanguage: string): LocalizedValue[] {
  const rank = (label: TermLabel): number => {
    const language = label.language?.toLowerCase() ?? null;
    if (language !== null && primaryLanguage(language) === primaryLanguage(preferredLanguage)) {
      return 0;
    }
    if (language !== null && primaryLanguage(language) === "en") {
      return 1;
    }
    return language === null ? 2 : 3;
  };
  return [...labels]
    .sort((left, right) => rank(left) - rank(right))
    .map((label) => (label.language === null ? { value: label.text } : { value: label.text, language: label.language }));
}

function primaryLanguage(tag: string): string {
  return tag.toLowerCase().split("-")[0] ?? tag;
}
